/**
 * FES2014 stations: loads the small per-location constituent JSON that
 * scripts/extract_fes.py writes (public/tides/fes/stations.json), and turns the
 * offline predictor into the tide-reading shapes the app already uses (a
 * TideHeightState for the height card, a set/rate for the streams). Absent file
 * (FES data not supplied yet) is not an error: no stations, callers fall back.
 */
import {
  predictHeightM,
  predictCurrent,
  type FesStation,
} from './fes';
import type { TideHeightState, HeightEvent, SpringLabel } from './heights';

let stationsCache: Promise<FesStation[]> | null = null;

export async function loadFesStations(): Promise<FesStation[]> {
  if (!stationsCache) {
    const base = import.meta.env.BASE_URL ?? '/';
    stationsCache = fetch(`${base}tides/fes/stations.json`)
      .then(async (r) => {
        if (!r.ok) return [];
        const d: unknown = await r.json();
        const arr = (d as { stations?: unknown })?.stations;
        return Array.isArray(arr) ? (arr as FesStation[]) : [];
      })
      .catch(() => []);
  }
  return stationsCache;
}

/** The loaded FES station nearest a position, or null when none are loaded. */
export function nearestFesStation(stations: FesStation[], pos: [number, number]): FesStation | null {
  let best: FesStation | null = null;
  let bestD = Infinity;
  for (const s of stations) {
    const dx = s.position[0] - pos[0];
    const dy = s.position[1] - pos[1];
    const d = dx * dx + dy * dy;
    if (d < bestD) {
      bestD = d;
      best = s;
    }
  }
  return best;
}

function spring(label: SpringLabel): SpringLabel {
  return label;
}

/** A TideHeightState from the FES harmonic curve: height now + bracketing HW/LW. */
export function fesHeightStateAt(st: FesStation, ms: number): TideHeightState {
  const heightM = predictHeightM(st, ms);
  // Scan a tide window for the bracketing turning points (prev/next HW or LW).
  const STEP = 5 * 60_000; // 5 min
  const span = 13 * 3_600_000;
  const sample = (t: number) => predictHeightM(st, t);
  // walk back to the previous extreme
  const findExtreme = (dir: 1 | -1): HeightEvent => {
    let t = ms;
    let prevV = sample(t);
    let slope = sample(t + dir * STEP) - prevV;
    for (let i = 0; i < span / STEP; i++) {
      const tn = t + dir * STEP;
      const v = sample(tn);
      const ns = v - prevV;
      if (Math.sign(ns) !== Math.sign(slope) && slope !== 0) {
        // turning point at t
        return { type: ns < 0 || slope > 0 ? 'H' : 'L', t, heightM: prevV };
      }
      slope = ns;
      prevV = v;
      t = tn;
    }
    return { type: 'H', t, heightM: prevV };
  };
  const prev = findExtreme(-1);
  const next = findExtreme(1);
  // Fix the H/L typing from the actual heights either side.
  prev.type = prev.heightM >= heightM ? 'H' : 'L';
  next.type = next.heightM >= heightM ? 'H' : 'L';
  const rising = next.type === 'H';
  // Spring/neap proxy: this cycle's range vs the model's maximum possible range.
  const maxRange = 2 * st.height.reduce((s, c) => s + Math.abs(c.ampM), 0);
  const range = Math.abs(next.heightM - prev.heightM);
  const springRatio = maxRange > 0 ? Math.min(1, range / maxRange) : 0.5;
  const label: SpringLabel = springRatio >= 0.66 ? 'Springs' : springRatio <= 0.33 ? 'Neaps' : 'Mid';
  return { heightM, rising, prev, next, springRatio, springLabel: spring(label) };
}

/** Set/rate from the FES current constituents at a station. */
export function fesStreamAt(st: FesStation, ms: number): { setDeg: number; rateKn: number } {
  return predictCurrent(st, ms);
}
